"use client"

import Header from "@/components/header"
import Footer from "@/components/footer"
import { StarfieldBackground } from "@/components/starfield-background"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import {
    FileSearch,
    CheckCircle2,
    ArrowRight,
    ArrowLeft,
    Loader2,
    Sparkles,
    Shield,
    Zap,
    Crown,
    Copy,
    Check
} from "lucide-react"
import Link from "next/link"
import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"

// Payment address from env
const PAYMENT_ADDRESS = process.env.NEXT_PUBLIC_AUDIT_PAYMENT_ADDRESS || "0xe0655C27132AD070426b7E08bd13797486f925EA"

// Crypto icons as SVG components
const EthIcon = () => (
    <svg viewBox="0 0 32 32" className="w-full h-full">
        <g fill="none" fillRule="evenodd">
            <circle fill="#627EEA" cx="16" cy="16" r="16" />
            <g fill="#FFF" fillRule="nonzero">
                <path fillOpacity=".602" d="M16.498 4v8.87l7.497 3.35z" />
                <path d="M16.498 4L9 16.22l7.498-3.35z" />
                <path fillOpacity=".602" d="M16.498 21.968v6.027L24 17.616z" />
                <path d="M16.498 27.995v-6.028L9 17.616z" />
                <path fillOpacity=".2" d="M16.498 20.573l7.497-4.353-7.497-3.348z" />
                <path fillOpacity=".602" d="M9 16.22l7.498 4.353v-7.701z" />
            </g>
        </g>
    </svg>
)

const BnbIcon = () => (
    <svg viewBox="0 0 32 32" className="w-full h-full">
        <g fill="none">
            <circle fill="#F3BA2F" cx="16" cy="16" r="16" />
            <path fill="#FFF" d="M12.116 14.404L16 10.52l3.886 3.886 2.26-2.26L16 6l-6.144 6.144 2.26 2.26zM6 16l2.26-2.26L10.52 16l-2.26 2.26L6 16zm6.116 1.596L16 21.48l3.886-3.886 2.26 2.259L16 26l-6.144-6.144-.003-.003 2.263-2.257zM21.48 16l2.26-2.26L26 16l-2.26 2.26L21.48 16zm-3.188-.002h.002V16L16 18.294l-2.291-2.29-.004-.004.004-.003.401-.402.195-.195L16 13.706l2.293 2.293z" />
        </g>
    </svg>
)

const UsdtIcon = () => (
    <svg viewBox="0 0 32 32" className="w-full h-full">
        <g fill="none" fillRule="evenodd">
            <circle fill="#26A17B" cx="16" cy="16" r="16" />
            <path fill="#FFF" d="M17.922 17.383v-.002c-.11.008-.677.042-1.942.042-1.01 0-1.721-.03-1.971-.042v.003c-3.888-.171-6.79-.848-6.79-1.658 0-.809 2.902-1.486 6.79-1.66v2.644c.254.018.982.061 1.988.061 1.207 0 1.812-.05 1.925-.06v-2.643c3.88.173 6.775.85 6.775 1.658 0 .81-2.895 1.485-6.775 1.657m0-3.59v-2.366h5.414V7.819H8.595v3.608h5.414v2.365c-4.4.202-7.709 1.074-7.709 2.118 0 1.044 3.309 1.915 7.709 2.118v7.582h3.913v-7.584c4.393-.202 7.694-1.073 7.694-2.116 0-1.043-3.301-1.914-7.694-2.117" />
        </g>
    </svg>
)

const PolIcon = () => (
    <svg viewBox="0 0 32 32" className="w-full h-full">
        <g fill="none">
            <circle fill="#8247E5" cx="16" cy="16" r="16" />
            <path fill="#FFF" d="M21.092 12.693c-.369-.215-.848-.215-1.254 0l-2.879 1.654-1.955 1.078-2.879 1.653c-.369.216-.848.216-1.254 0l-2.288-1.294c-.369-.215-.627-.61-.627-1.042V12.19c0-.431.221-.826.627-1.042l2.25-1.258c.37-.216.85-.216 1.256 0l2.25 1.258c.37.216.628.611.628 1.042v1.654l1.955-1.115v-1.653a1.16 1.16 0 00-.627-1.042l-4.17-2.372c-.369-.216-.848-.216-1.254 0l-4.244 2.372A1.16 1.16 0 006 11.076v4.78c0 .432.221.827.627 1.043l4.244 2.372c.369.215.849.215 1.254 0l2.879-1.618 1.955-1.114 2.879-1.617c.369-.216.848-.216 1.254 0l2.251 1.258c.37.215.627.61.627 1.042v2.552c0 .431-.22.826-.627 1.042l-2.25 1.294c-.37.216-.85.216-1.255 0l-2.251-1.258c-.37-.216-.628-.611-.628-1.042v-1.654l-1.955 1.115v1.653c0 .431.221.827.627 1.042l4.244 2.372c.369.216.848.216 1.254 0l4.244-2.372c.369-.215.627-.61.627-1.042v-4.78a1.16 1.16 0 00-.627-1.042l-4.28-2.409z" />
        </g>
    </svg>
)

const paymentTokens = [
    {
        symbol: "ETH",
        name: "Ethereum",
        chainId: 1,
        icon: EthIcon,
        decimals: 18,
        isNative: true
    },
    {
        symbol: "BNB",
        name: "BNB Chain",
        chainId: 56,
        icon: BnbIcon,
        decimals: 18,
        isNative: true
    },
    {
        symbol: "POL",
        name: "Polygon",
        chainId: 137,
        icon: PolIcon,
        decimals: 18,
        isNative: true
    },
    {
        symbol: "USDT",
        name: "Tether USD",
        chainId: 1,
        icon: UsdtIcon,
        decimals: 6,
        isNative: false
    }
]

const packages = [
    {
        id: "basic",
        name: "Basic Audit",
        price: "FREE",
        priceUsd: 0,
        description: "Limited Time Offer",
        icon: Shield,
        color: "from-green-500 to-emerald-600",
        borderColor: "border-green-500/50",
        bgColor: "bg-green-500/10",
        features: [
            "Up to 300 lines of code",
            "Automated vulnerability scan",
            "Basic code review",
            "PDF audit report",
            "7-10 day turnaround"
        ],
        isFree: true,
        badge: "Limited Time"
    },
    {
        id: "standard",
        name: "Standard Audit",
        price: "$200",
        priceUsd: 200,
        description: "For presale & token contracts",
        icon: Zap,
        color: "from-blue-500 to-cyan-600",
        borderColor: "border-blue-500/50",
        bgColor: "bg-blue-500/10",
        features: [
            "Up to 1,000 lines of code",
            "Comprehensive analysis",
            "Manual code review",
            "Gas optimization tips",
            "Audit badge for project",
            "5-7 day turnaround",
            "1 re-audit included"
        ],
        popular: true
    },
    {
        id: "premium",
        name: "Premium Audit",
        price: "$600",
        priceUsd: 600,
        description: "For complex DeFi protocols",
        icon: Crown,
        color: "from-purple-500 to-pink-600",
        borderColor: "border-purple-500/50",
        bgColor: "bg-purple-500/10",
        features: [
            "Unlimited lines of code",
            "Full protocol analysis",
            "Economic model review",
            "Priority support",
            "48-hour express option",
            "Unlimited re-audits",
            "Featured listing"
        ]
    }
]

export function AuditRequestContent() {
    const [step, setStep] = useState(1)
    const [selectedPackage, setSelectedPackage] = useState<typeof packages[0] | null>(null)
    const [selectedToken, setSelectedToken] = useState(paymentTokens[0])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [copied, setCopied] = useState(false)
    const [txHash, setTxHash] = useState("")
    const [formData, setFormData] = useState({
        email: "",
        projectName: "",
        contractAddress: "",
        description: ""
    })

    const copyAddress = () => {
        navigator.clipboard.writeText(PAYMENT_ADDRESS)
        setCopied(true)
        toast.success("Payment address copied!")
        setTimeout(() => setCopied(false), 2000)
    }

    const handleSelectPackage = (pkg: typeof packages[0]) => {
        setSelectedPackage(pkg)
        setStep(2)
    }

    const handleSubmit = async () => {
        if (!formData.email || !formData.projectName) {
            toast.error("Please fill in all required fields")
            return
        }

        if (!selectedPackage?.isFree && !txHash) {
            toast.error("Please enter your transaction hash")
            return
        }

        setIsSubmitting(true)

        try {
            const response = await fetch("/api/audit/request", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    plan: selectedPackage?.name,
                    amount: selectedPackage?.priceUsd,
                    paymentToken: selectedPackage?.isFree ? null : selectedToken.symbol,
                    txHash: selectedPackage?.isFree ? null : txHash
                })
            })

            if (!response.ok) throw new Error("Failed to submit")

            toast.success(
                selectedPackage?.isFree
                    ? "Audit request submitted successfully!"
                    : "Audit request submitted! We'll verify your payment shortly."
            )
            setStep(3)
        } catch {
            toast.error("Failed to submit audit request")
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className="min-h-screen flex flex-col relative">
            <StarfieldBackground />
            <Header />
            <main className="flex-1 relative z-10 py-20">
                <div className="container max-w-5xl">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center mb-12"
                    >
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-sm font-medium text-blue-500">
                            <FileSearch className="h-4 w-4" />
                            Request Smart Contract Audit
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-4">
                            Secure Your Smart Contract
                        </h1>
                        <p className="text-muted-foreground max-w-xl mx-auto">
                            Professional auditing to identify vulnerabilities and build investor confidence
                        </p>
                    </motion.div>

                    {/* Progress Steps */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="flex items-center justify-center gap-4 mb-12"
                    >
                        {[1, 2, 3].map((s) => (
                            <div key={s} className="flex items-center gap-2">
                                <motion.div
                                    animate={{
                                        scale: step === s ? 1.1 : 1,
                                        backgroundColor: step >= s ? "rgb(59 130 246)" : "rgb(39 39 42)"
                                    }}
                                    className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white"
                                >
                                    {step > s ? <Check className="w-5 h-5" /> : s}
                                </motion.div>
                                <span className={`text-sm hidden sm:block ${step >= s ? "text-foreground" : "text-muted-foreground"}`}>
                                    {s === 1 ? "Choose Package" : s === 2 ? "Details & Payment" : "Confirmation"}
                                </span>
                                {s < 3 && <div className="w-8 h-0.5 bg-border" />}
                            </div>
                        ))}
                    </motion.div>

                    <AnimatePresence mode="wait">
                        {/* Step 1: Package Selection */}
                        {step === 1 && (
                            <motion.div
                                key="step1"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="grid gap-6 md:grid-cols-3"
                            >
                                {packages.map((pkg, index) => {
                                    const Icon = pkg.icon
                                    return (
                                        <motion.div
                                            key={pkg.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <Card
                                                className={`relative cursor-pointer transition-all duration-300 hover:scale-[1.02] ${pkg.popular ? "border-blue-500 shadow-lg shadow-blue-500/20" : "border-border/40 hover:border-primary/50"
                                                    }`}
                                                onClick={() => handleSelectPackage(pkg)}
                                            >
                                                {pkg.popular && (
                                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                        <Badge className="bg-blue-500 text-white">Most Popular</Badge>
                                                    </div>
                                                )}
                                                {pkg.badge && (
                                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                        <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white flex items-center gap-1">
                                                            <Sparkles className="h-3 w-3" />
                                                            {pkg.badge}
                                                        </Badge>
                                                    </div>
                                                )}
                                                <CardContent className="p-6">
                                                    <div className="text-center mb-6">
                                                        <motion.div
                                                            whileHover={{ rotate: 5, scale: 1.1 }}
                                                            className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br ${pkg.color} flex items-center justify-center`}
                                                        >
                                                            <Icon className="w-8 h-8 text-white" />
                                                        </motion.div>
                                                        <h3 className="text-xl font-bold mb-2">{pkg.name}</h3>
                                                        <div className={`text-4xl font-bold mb-2 bg-gradient-to-r ${pkg.color} bg-clip-text text-transparent`}>
                                                            {pkg.price}
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">{pkg.description}</p>
                                                    </div>
                                                    <ul className="space-y-3 mb-6">
                                                        {pkg.features.map((feature, i) => (
                                                            <motion.li
                                                                key={i}
                                                                initial={{ opacity: 0, x: -10 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                transition={{ delay: 0.2 + i * 0.05 }}
                                                                className="flex items-center gap-2 text-sm"
                                                            >
                                                                <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />
                                                                <span>{feature}</span>
                                                            </motion.li>
                                                        ))}
                                                    </ul>
                                                    <Button
                                                        className={`w-full bg-gradient-to-r ${pkg.color} hover:opacity-90`}
                                                    >
                                                        {pkg.isFree ? "Get Free Audit" : "Select Package"}
                                                        <ArrowRight className="ml-2 h-4 w-4" />
                                                    </Button>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    )
                                })}
                            </motion.div>
                        )}

                        {/* Step 2: Details & Payment */}
                        {step === 2 && selectedPackage && (
                            <motion.div
                                key="step2"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="max-w-2xl mx-auto"
                            >
                                <Card className="border-border/40">
                                    <CardContent className="p-8">
                                        {/* Selected Package Summary */}
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className={`p-4 rounded-xl ${selectedPackage.bgColor} ${selectedPackage.borderColor} border mb-8`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${selectedPackage.color} flex items-center justify-center`}>
                                                    <selectedPackage.icon className="w-6 h-6 text-white" />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold">{selectedPackage.name}</h3>
                                                    <p className={`text-lg font-bold bg-gradient-to-r ${selectedPackage.color} bg-clip-text text-transparent`}>
                                                        {selectedPackage.price}
                                                    </p>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="ml-auto"
                                                    onClick={() => setStep(1)}
                                                >
                                                    Change
                                                </Button>
                                            </div>
                                        </motion.div>

                                        {/* Project Details Form */}
                                        <div className="space-y-6">
                                            <h4 className="font-semibold text-lg">Project Details</h4>
                                            <div className="grid gap-4">
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.1 }}
                                                    className="space-y-2"
                                                >
                                                    <label className="text-sm font-medium">Email *</label>
                                                    <Input
                                                        type="email"
                                                        placeholder="your@email.com"
                                                        value={formData.email}
                                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    />
                                                </motion.div>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.15 }}
                                                    className="space-y-2"
                                                >
                                                    <label className="text-sm font-medium">Project Name *</label>
                                                    <Input
                                                        placeholder="My Token"
                                                        value={formData.projectName}
                                                        onChange={(e) => setFormData({ ...formData, projectName: e.target.value })}
                                                    />
                                                </motion.div>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.2 }}
                                                    className="space-y-2"
                                                >
                                                    <label className="text-sm font-medium">Contract Address (optional)</label>
                                                    <Input
                                                        placeholder="0x..."
                                                        value={formData.contractAddress}
                                                        onChange={(e) => setFormData({ ...formData, contractAddress: e.target.value })}
                                                    />
                                                </motion.div>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.25 }}
                                                    className="space-y-2"
                                                >
                                                    <label className="text-sm font-medium">Description</label>
                                                    <Textarea
                                                        placeholder="Brief description of your project..."
                                                        value={formData.description}
                                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                                    />
                                                </motion.div>
                                            </div>

                                            {/* Payment Section (only for paid plans) */}
                                            {!selectedPackage.isFree && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.3 }}
                                                    className="pt-6 border-t space-y-6"
                                                >
                                                    <h4 className="font-semibold text-lg">Payment</h4>

                                                    <div className="space-y-3">
                                                        <label className="text-sm font-medium">Select Payment Token</label>
                                                        <div className="grid grid-cols-4 gap-3">
                                                            {paymentTokens.map((token) => {
                                                                const TokenIcon = token.icon
                                                                return (
                                                                    <motion.button
                                                                        key={token.symbol}
                                                                        whileHover={{ scale: 1.05 }}
                                                                        whileTap={{ scale: 0.95 }}
                                                                        onClick={() => setSelectedToken(token)}
                                                                        className={`p-4 rounded-xl border text-center transition-all ${selectedToken.symbol === token.symbol
                                                                            ? "border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20"
                                                                            : "border-border hover:border-blue-500/50"
                                                                            }`}
                                                                    >
                                                                        <div className="w-10 h-10 mx-auto mb-2">
                                                                            <TokenIcon />
                                                                        </div>
                                                                        <div className="text-sm font-medium">{token.symbol}</div>
                                                                    </motion.button>
                                                                )
                                                            })}
                                                        </div>
                                                    </div>

                                                    <motion.div
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        className="p-4 rounded-xl bg-muted/50 border space-y-4"
                                                    >
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-muted-foreground">Amount:</span>
                                                            <span className={`text-xl font-bold bg-gradient-to-r ${selectedPackage.color} bg-clip-text text-transparent`}>
                                                                {selectedPackage.price}
                                                            </span>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <label className="text-sm text-muted-foreground">Send {selectedToken.symbol} to:</label>
                                                            <div className="flex items-center gap-2 p-3 rounded-lg bg-background border">
                                                                <code className="text-xs flex-1 truncate font-mono">{PAYMENT_ADDRESS}</code>
                                                                <Button
                                                                    size="icon"
                                                                    variant="ghost"
                                                                    className="h-8 w-8 shrink-0"
                                                                    onClick={copyAddress}
                                                                >
                                                                    {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    </motion.div>

                                                    <div className="space-y-2">
                                                        <label className="text-sm font-medium">Transaction Hash *</label>
                                                        <Input
                                                            placeholder="0x..."
                                                            value={txHash}
                                                            onChange={(e) => setTxHash(e.target.value)}
                                                            className="font-mono text-sm"
                                                        />
                                                        <p className="text-xs text-muted-foreground">
                                                            Send the payment first, then paste the transaction hash here
                                                        </p>
                                                    </div>
                                                </motion.div>
                                            )}

                                            {/* Actions */}
                                            <div className="flex gap-4 pt-4">
                                                <Button
                                                    variant="outline"
                                                    onClick={() => setStep(1)}
                                                    className="gap-2"
                                                >
                                                    <ArrowLeft className="h-4 w-4" />
                                                    Back
                                                </Button>
                                                <Button
                                                    className={`flex-1 bg-gradient-to-r ${selectedPackage.color} hover:opacity-90`}
                                                    onClick={handleSubmit}
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                            Submitting...
                                                        </>
                                                    ) : (
                                                        <>
                                                            Submit Audit Request
                                                            <ArrowRight className="ml-2 h-4 w-4" />
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        )}

                        {/* Step 3: Confirmation */}
                        {step === 3 && (
                            <motion.div
                                key="step3"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="max-w-lg mx-auto text-center"
                            >
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", delay: 0.2 }}
                                    className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center"
                                >
                                    <Check className="w-12 h-12 text-white" />
                                </motion.div>
                                <motion.h2
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="text-2xl font-bold mb-4"
                                >
                                    Audit Request Submitted!
                                </motion.h2>
                                <motion.p
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="text-muted-foreground mb-8"
                                >
                                    {selectedPackage?.isFree
                                        ? "Our team will review your request and contact you within 24-48 hours."
                                        : "We will verify your payment and contact you within 24 hours to begin the audit process."}
                                </motion.p>
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                    className="flex flex-col sm:flex-row gap-4 justify-center"
                                >
                                    <Link href="/audit">
                                        <Button variant="outline" className="gap-2">
                                            <ArrowLeft className="h-4 w-4" />
                                            Back to Audit
                                        </Button>
                                    </Link>
                                    <Link href="/">
                                        <Button className="bg-blue-500 hover:bg-blue-600">
                                            Go to Homepage
                                        </Button>
                                    </Link>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </main>

            {/* SEO explainer — below the content, above footer */}
            <section className="container max-w-3xl mx-auto pb-16">
                <div className="rounded-xl bg-muted/30 p-6 text-sm text-muted-foreground space-y-3">
                    <h2 className="text-base font-semibold text-foreground">Why Audit Your Smart Contract?</h2>
                    <p>
                        A smart contract audit checks your code for bugs, security holes, and gas inefficiencies
                        before your project goes live. Audited contracts give investors confidence that their
                        funds are safe.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong>Basic audit (free)</strong> — automated scan for common vulnerabilities and code quality.</li>
                        <li><strong>Standard audit</strong> — manual code review by experienced blockchain auditors.</li>
                        <li><strong>Premium audit</strong> — full review with a detailed report and remediation guidance.</li>
                    </ul>
                    <p>
                        After your audit passes, your project earns an Audit badge that is displayed on your
                        presale page. This badge helps your project stand out and attract more investors.
                        Most audits are completed within 24 to 48 hours.
                    </p>
                </div>
            </section>

            <Footer />
        </div>
    )
}
